import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight, MapPin, Calendar, Clock, Search, Briefcase, Plane, Building2,
  Compass, ShieldCheck, Star, Quote, PhoneCall, ChevronRight, Sparkles,
} from "lucide-react";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import Logo from "../components/Logo";
import PhoneMockup from "../components/PhoneMockup";
import DarkMapMockup from "../components/DarkMapMockup";
import StatusBadge from "../components/StatusBadge";
import { api } from "@/lib/api";
import { services, howItWorks, stats, testimonials, optimizedRoute } from "../lib/mockData";

const HERO_IMG = "https://images.unsplash.com/photo-1592309905620-e5b59f6dcb98?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1MTN8MHwxfHNlYXJjaHwzfHxibGFjayUyMG1lcmNlZGVzJTIwYmVueiUyMGV4ZWN1dGl2ZSUyMGNhcnxlbnwwfHx8fDE3ODMzNjI3MTh8MA&ixlib=rb-4.1.0&q=85";

const iconMap = { briefcase: Briefcase, plane: Plane, building: Building2, map: Compass };

const Landing = () => {
  const [publicRoutes, setPublicRoutes] = useState([]);

  useEffect(() => {
    api
      .get("/api/rutas")
      .then(({ data }) => setPublicRoutes(data))
      .catch(() => setPublicRoutes([]));
  }, []);

  return (
    <div className="min-h-screen bg-[#0D0E10] text-[#F5F5F5] grain">
      <Navbar />

      {/* HERO */}
      <section className="relative overflow-hidden" data-testid="hero-section">
        <div className="absolute inset-0">
          <img src={HERO_IMG} alt="" className="w-full h-full object-cover opacity-40" />
          <div className="absolute inset-0 bg-gradient-to-b from-[#0D0E10]/70 via-[#0D0E10]/90 to-[#0D0E10]" />
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,rgba(201,162,39,0.15),transparent_50%)]" />
        </div>

        <div className="relative max-w-7xl mx-auto px-6 lg:px-12 pt-24 pb-40 lg:pt-32 lg:pb-56">
          <div className="max-w-3xl animate-fade-in">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 border border-[#C9A227]/40 rounded-full mb-8">
              <Sparkles size={12} className="text-[#C9A227]" />
              <span className="text-[10px] uppercase tracking-brand font-display text-[#C9A227]">
                Servicio ejecutivo Lima · San Isidro · Miraflores
              </span>
            </div>

            <h1 className="font-display font-bold text-4xl sm:text-5xl lg:text-7xl leading-[1.05] uppercase tracking-tight">
              Traslados <br />
              <span className="text-[#C9A227]">Ejecutivos</span> <br />
              Premium
            </h1>

            <p className="mt-8 text-base lg:text-lg text-[#A0A0A5] font-body leading-relaxed max-w-xl">
              Sedanes negros. Conductores uniformados. Puntualidad de aviación privada.
              Reserva tu traslado aeropuerto-ciudad con la discreción y elegancia que tu agenda merece.
            </p>

            <div className="mt-10 flex flex-col sm:flex-row items-start sm:items-center gap-4">
              <Link
                to="/cliente"
                className="group inline-flex items-center gap-3 px-7 py-4 bg-[#C9A227] hover:bg-[#D4AF37] text-[#0D0E10] font-display font-bold uppercase tracking-wider text-sm rounded-lg transition-all"
                data-testid="hero-reserve-btn"
              >
                Reservar ahora
                <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
              </Link>
              <a
                href="#como-funciona"
                className="inline-flex items-center gap-3 px-7 py-4 border border-[#2E2F33] hover:border-[#C9A227] text-[#F5F5F5] hover:text-[#C9A227] font-display font-semibold uppercase tracking-wider text-sm rounded-lg transition-all"
                data-testid="hero-how-btn"
              >
                Cómo funciona
              </a>
            </div>
          </div>
        </div>

        {/* Floating quick search */}
        <div className="relative max-w-6xl mx-auto px-6 lg:px-12 -mt-24 lg:-mt-32 z-10">
          <QuickSearch />
        </div>
      </section>

      {/* SERVICES */}
      <section id="servicios" className="relative py-24 lg:py-32">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <SectionHeader eyebrow="Servicios" title="Cada viaje, una experiencia curada" desc="Cuatro líneas de servicio diseñadas para agendas ejecutivas exigentes." />

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mt-12">
            {services.map((s) => {
              const Icon = iconMap[s.icon];
              return (
                <div
                  key={s.id}
                  className="group relative bg-[#1C1D20] border border-[#2E2F33] hover:border-[#C9A227]/50 rounded-xl p-7 transition-all duration-300 hover:-translate-y-1"
                  data-testid={`service-card-${s.id}`}
                >
                  <div className="w-12 h-12 flex items-center justify-center border border-[#C9A227]/30 rounded-lg mb-6 group-hover:border-[#C9A227] transition">
                    <Icon size={20} className="text-[#C9A227]" />
                  </div>
                  <h3 className="font-display font-bold text-lg uppercase tracking-wide mb-3">{s.title}</h3>
                  <p className="text-sm text-[#A0A0A5] font-body leading-relaxed">{s.desc}</p>
                  <div className="mt-6 inline-flex items-center gap-2 text-xs text-[#C9A227] uppercase tracking-wider font-display font-semibold">
                    Ver más <ChevronRight size={12} />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section id="como-funciona" className="relative py-24 lg:py-32 bg-[#0A0B0D] border-y border-[#2E2F33]">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <SectionHeader eyebrow="Proceso" title="Reserva en tres pasos" desc="De la solicitud a la puerta del auto, en menos de un minuto." />

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-12">
            {howItWorks.map((h, i) => (
              <div key={h.step} className="relative bg-[#1C1D20] border border-[#2E2F33] rounded-xl p-8" data-testid={`how-step-${i}`}>
                <div className="flex items-baseline gap-4 mb-6">
                  <span className="font-display font-bold text-5xl text-[#C9A227] tabular-nums">{h.step}</span>
                  <span className="h-px flex-1 bg-[#2E2F33]" />
                </div>
                <h3 className="font-display font-bold text-xl uppercase tracking-wide mb-3">{h.title}</h3>
                <p className="text-sm text-[#A0A0A5] font-body leading-relaxed">{h.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ROUTES */}
      <section id="rutas" className="py-24 lg:py-32">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <SectionHeader
            eyebrow="Rutas disponibles"
            title="Aeropuerto Jorge Chávez y distritos ejecutivos"
            desc="Tarifas fijas. Sin sobreprecios nocturnos ni por tráfico."
          />

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mt-12">
            {publicRoutes.length === 0 && (
              <p className="text-sm text-[#A0A0A5] font-body col-span-full">
                Todavía no hay rutas publicadas por nuestros conductores.
              </p>
            )}
            {publicRoutes.slice(0, 6).map((r) => {
              const origin = r.direction === "aeropuerto_ciudad" ? r.airport_name : r.city;
              const destination = r.direction === "aeropuerto_ciudad" ? r.city : r.airport_name;
              return (
                <div
                  key={r.id}
                  className="group bg-[#1C1D20] border border-[#2E2F33] hover:border-[#C9A227]/50 rounded-xl p-6 transition-all"
                  data-testid={`route-card-${r.id}`}
                >
                  <div className="flex items-start justify-between mb-5">
                    <div className="flex flex-col gap-2 min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <div className="w-2 h-2 rounded-full border border-[#C9A227]" />
                        <p className="text-sm font-body text-[#F5F5F5] truncate">{origin}</p>
                      </div>
                      <div className="flex items-center gap-2 pl-1">
                        <span className="w-px h-4 bg-[#2E2F33]" />
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="w-2 h-2 rounded-full bg-[#C9A227]" />
                        <p className="text-sm font-body text-[#F5F5F5] truncate">{destination}</p>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center justify-between pt-4 border-t border-[#2E2F33]">
                    <div className="flex items-center gap-4 text-xs text-[#A0A0A5] font-body">
                      <span className="flex items-center gap-1"><Clock size={12} /> {r.estimated_duration_minutes} min</span>
                      <span className="flex items-center gap-1"><MapPin size={12} /> {r.estimated_distance_km} km</span>
                    </div>
                    <div className="text-right">
                      <p className="text-[10px] uppercase tracking-wider text-[#A0A0A5]">Desde</p>
                      <p className="font-display font-bold text-lg text-[#C9A227]">
                        S/ {r.private_enabled ? r.private_price : r.price_per_seat}
                        {r.shared_enabled && !r.private_enabled && <span className="text-[10px] text-[#A0A0A5]"> /asiento</span>}
                      </p>
                    </div>
                  </div>
                  <div className="mt-3 flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      {r.private_enabled && (
                        <span className="px-2 py-0.5 border border-[#2E2F33] rounded-full text-[9px] uppercase tracking-wider text-[#A0A0A5]">Privado</span>
                      )}
                      {r.shared_enabled && (
                        <span className="px-2 py-0.5 border border-[#2E2F33] rounded-full text-[9px] uppercase tracking-wider text-[#A0A0A5]">Compartido</span>
                      )}
                    </div>
                    {r.driver_rating_average != null ? (
                      <span className="flex items-center gap-1 text-[10px] text-[#C9A227] font-body">
                        <Star size={11} fill="currentColor" /> {r.driver_rating_average.toFixed(1)} ({r.driver_rating_count})
                      </span>
                    ) : (
                      <span className="text-[10px] text-[#A0A0A5] font-body">Sin reseñas</span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* STATS */}
      <section className="py-16 lg:py-20 border-y border-[#2E2F33] bg-[#0A0B0D]">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
            {stats.map((s) => (
              <div key={s.label} className="text-center lg:text-left" data-testid={`stat-${s.label.replace(/\s+/g, "-").toLowerCase()}`}>
                <p className="font-display font-bold text-4xl lg:text-5xl text-[#F5F5F5]">{s.value}</p>
                <p className="mt-2 text-xs uppercase tracking-brand text-[#C9A227] font-display">{s.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* MOBILE APP SHOWCASE */}
      <section id="showcase" className="py-24 lg:py-32">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <SectionHeader
            eyebrow="App móvil · PWA"
            title="La experiencia LEP en tu bolsillo"
            desc="Instala la Progressive Web App y accede a tus reservas, mapa y ticket ejecutivo con un toque."
          />

          <div className="mt-16 overflow-x-auto pb-6 lg:overflow-visible">
            <div className="flex lg:grid lg:grid-cols-3 xl:grid-cols-4 gap-10 lg:gap-6 min-w-max lg:min-w-0 justify-center">
              <MobileMockScreen1 />
              <MobileMockScreen2 />
              <MobileMockScreen3 />
              <MobileMockScreen4 />
            </div>
          </div>

          <div className="mt-14 flex flex-col md:flex-row items-center justify-between gap-6 p-8 border border-[#C9A227]/30 rounded-xl bg-[#1C1D20]/60">
            <div>
              <p className="text-[10px] uppercase tracking-brand text-[#C9A227] font-display mb-2">Instalable como app</p>
              <h4 className="font-display font-bold text-2xl">Añade LEP a tu pantalla de inicio</h4>
              <p className="text-sm text-[#A0A0A5] mt-2 font-body">iOS y Android · Sin descargas · Actualización silenciosa</p>
            </div>
            <Link to="/cliente" className="px-6 py-3.5 bg-[#C9A227] hover:bg-[#D4AF37] text-[#0D0E10] font-display font-bold uppercase tracking-wider text-sm rounded-lg" data-testid="showcase-install-btn">
              Probar la app
            </Link>
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section id="nosotros" className="py-24 lg:py-32 bg-[#0A0B0D] border-t border-[#2E2F33]">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <SectionHeader eyebrow="Testimonios" title="Ejecutivos que ya confían" />
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-12">
            {testimonials.map((t, i) => (
              <div key={i} className="bg-[#1C1D20] border border-[#2E2F33] rounded-xl p-8" data-testid={`testimonial-${i}`}>
                <Quote size={20} className="text-[#C9A227] mb-5" />
                <p className="text-sm text-[#F5F5F5] font-body leading-relaxed mb-6">&ldquo;{t.quote}&rdquo;</p>
                <div className="flex items-center gap-3 pt-5 border-t border-[#2E2F33]">
                  <div className="w-10 h-10 rounded-full bg-[#C9A227]/15 border border-[#C9A227]/40 flex items-center justify-center text-[#C9A227] font-display font-bold text-sm">
                    {t.name.split(" ").map((s) => s[0]).join("").slice(0, 2)}
                  </div>
                  <div>
                    <p className="text-sm font-display font-semibold">{t.name}</p>
                    <p className="text-xs text-[#A0A0A5]">{t.role}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 lg:py-32">
        <div className="max-w-4xl mx-auto px-6 lg:px-12 text-center">
          <ShieldCheck size={32} className="text-[#C9A227] mx-auto mb-6" />
          <h2 className="font-display font-bold text-3xl lg:text-5xl uppercase tracking-tight">
            Tu próximo traslado <br /> merece <span className="text-[#C9A227]">discreción</span>.
          </h2>
          <p className="mt-6 text-[#A0A0A5] font-body max-w-xl mx-auto">
            Regístrate hoy y obtén tu primer traslado ejecutivo con 15% de bienvenida.
          </p>
          <div className="mt-8 flex items-center justify-center gap-3 flex-wrap">
            <Link to="/registro" className="px-7 py-4 bg-[#C9A227] hover:bg-[#D4AF37] text-[#0D0E10] font-display font-bold uppercase tracking-wider text-sm rounded-lg" data-testid="cta-register-btn">
              Crear cuenta
            </Link>
            <a href="tel:+51987000111" className="inline-flex items-center gap-2 px-7 py-4 border border-[#2E2F33] hover:border-[#C9A227] text-[#F5F5F5] hover:text-[#C9A227] font-display font-semibold uppercase tracking-wider text-sm rounded-lg" data-testid="cta-call-btn">
              <PhoneCall size={14} /> +51 987 000 111
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

const SectionHeader = ({ eyebrow, title, desc }) => (
  <div className="max-w-2xl">
    <p className="text-[10px] uppercase tracking-brand text-[#C9A227] font-display mb-4 flex items-center gap-3">
      <span className="h-px w-8 bg-[#C9A227]" />
      {eyebrow}
    </p>
    <h2 className="font-display font-bold text-3xl lg:text-5xl uppercase tracking-tight leading-tight">
      {title}
    </h2>
    {desc && <p className="mt-5 text-[#A0A0A5] font-body">{desc}</p>}
  </div>
);

const QuickSearch = () => {
  const [origin, setOrigin] = useState("Aeropuerto Jorge Chávez");
  const [destination, setDestination] = useState("Miraflores");

  return (
    <div className="bg-[#1C1D20]/95 backdrop-blur-md border border-[#C9A227]/40 rounded-2xl p-6 lg:p-8 shadow-[0_30px_80px_-20px_rgba(201,162,39,0.25)]" data-testid="quick-search-widget">
      <div className="flex items-center gap-3 mb-6">
        <Search size={16} className="text-[#C9A227]" />
        <p className="text-[10px] uppercase tracking-brand text-[#C9A227] font-display">Buscador rápido</p>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
        <InputBlock icon={MapPin} label="Origen" value={origin} onChange={setOrigin} testId="qs-origin" />
        <InputBlock icon={MapPin} label="Destino" value={destination} onChange={setDestination} testId="qs-destination" />
        <InputBlock icon={Calendar} label="Fecha" value="15 Dic 2025" testId="qs-date" />
        <InputBlock icon={Clock} label="Hora" value="18:30" testId="qs-time" />
        <Link to="/cliente/buscar" className="flex items-center justify-center gap-2 px-5 py-4 bg-[#C9A227] hover:bg-[#D4AF37] text-[#0D0E10] font-display font-bold uppercase tracking-wider text-sm rounded-lg transition-all" data-testid="qs-search-btn">
          Buscar
          <ArrowRight size={14} />
        </Link>
      </div>
    </div>
  );
};

const InputBlock = ({ icon: Icon, label, value, onChange, testId }) => (
  <label className="block bg-[#0D0E10] border border-[#2E2F33] hover:border-[#C9A227]/40 focus-within:border-[#C9A227] rounded-lg px-4 py-3 transition-all" data-testid={testId}>
    <span className="flex items-center gap-2 text-[10px] uppercase tracking-wider text-[#A0A0A5] font-display mb-1">
      <Icon size={11} className="text-[#C9A227]" /> {label}
    </span>
    <input
      value={value}
      onChange={(e) => onChange && onChange(e.target.value)}
      className="w-full bg-transparent outline-none text-sm text-[#F5F5F5] font-body"
    />
  </label>
);

// -------- Phone mockup screens ---------
const MobileMockScreen1 = () => (
  <PhoneMockup label="Splash · Marca">
    <div className="h-full flex flex-col items-center justify-center bg-[#0D0E10] px-8 relative overflow-hidden">
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,rgba(201,162,39,0.15),transparent_60%)]" />
      <div className="relative">
        <Logo size="lg" to={null} />
      </div>
      <div className="absolute bottom-16 flex items-center gap-2">
        <div className="w-6 h-1 rounded-full bg-[#C9A227]" />
        <div className="w-1 h-1 rounded-full bg-[#2E2F33]" />
        <div className="w-1 h-1 rounded-full bg-[#2E2F33]" />
      </div>
      <p className="absolute bottom-8 text-[9px] uppercase tracking-brand text-[#A0A0A5] font-display">Cargando experiencia</p>
    </div>
  </PhoneMockup>
);

const MobileMockScreen2 = () => (
  <PhoneMockup label="Dashboard · Cliente">
    <div className="h-full bg-[#0D0E10] flex flex-col">
      <div className="px-4 pt-2 pb-3">
        <p className="text-[9px] uppercase tracking-brand text-[#A0A0A5]">Buenas tardes</p>
        <p className="font-display font-bold text-base">Alejandra V.</p>
      </div>
      <div className="mx-4 rounded-xl bg-gradient-to-br from-[#1C1D20] to-[#0D0E10] border border-[#C9A227]/40 p-4 relative overflow-hidden">
        <div className="absolute -right-4 -top-4 w-16 h-16 rounded-full bg-[#C9A227]/10 blur-2xl" />
        <p className="text-[9px] uppercase tracking-brand text-[#C9A227] mb-1">Próximo viaje</p>
        <p className="font-display font-bold text-sm">Aeropuerto → Miraflores</p>
        <p className="text-[10px] text-[#A0A0A5] mt-1">Hoy · 18:30 · LA2405</p>
        <div className="flex items-center justify-between mt-4">
          <p className="font-display font-bold text-[#C9A227]">S/ 89</p>
          <StatusBadge status="Confirmado" />
        </div>
      </div>

      <div className="mx-4 mt-3 rounded-xl bg-[#C9A227] p-4 flex items-center justify-between">
        <div>
          <p className="text-[9px] uppercase tracking-brand text-[#0D0E10]/80">Nueva reserva</p>
          <p className="font-display font-bold text-xs text-[#0D0E10]">Reservar traslado</p>
        </div>
        <ArrowRight size={16} className="text-[#0D0E10]" />
      </div>

      <div className="px-4 mt-4">
        <p className="text-[9px] uppercase tracking-brand text-[#A0A0A5] mb-2">Últimos viajes</p>
        {[1, 2, 3].map((i) => (
          <div key={i} className="flex items-center justify-between py-2.5 border-b border-[#1C1D20]">
            <div>
              <p className="text-[10px] text-[#F5F5F5]">Aeropuerto → San Isidro</p>
              <p className="text-[9px] text-[#A0A0A5]">10 Dic · 22:15</p>
            </div>
            <p className="text-[10px] text-[#C9A227] font-display font-bold">S/ 85</p>
          </div>
        ))}
      </div>

      {/* Bottom nav */}
      <div className="mt-auto border-t border-[#1C1D20] px-2 py-2 flex items-center justify-around">
        {["Inicio", "Buscar", "Reservas", "Perfil"].map((l, i) => (
          <div key={l} className={`flex flex-col items-center gap-0.5 py-1 px-2 rounded-lg ${i === 0 ? "bg-[#C9A227]/10" : ""}`}>
            <div className={`w-4 h-4 rounded ${i === 0 ? "bg-[#C9A227]" : "bg-[#2E2F33]"}`} />
            <span className={`text-[7px] uppercase ${i === 0 ? "text-[#C9A227]" : "text-[#A0A0A5]"}`}>{l}</span>
          </div>
        ))}
      </div>
    </div>
  </PhoneMockup>
);

const MobileMockScreen3 = () => (
  <PhoneMockup label="Selección de destino · Mapa">
    <div className="h-full bg-[#0D0E10] flex flex-col relative">
      <div className="absolute top-2 left-3 right-3 z-20 bg-[#1C1D20] border border-[#2E2F33] rounded-lg px-3 py-2.5 flex items-center gap-2">
        <Search size={12} className="text-[#C9A227]" />
        <p className="text-[10px] text-[#F5F5F5] font-body">Av. Larco 1250, Miraflores</p>
      </div>
      <div className="flex-1 relative">
        <DarkMapMockup variant="destination" pin={{ x: 400, y: 240 }} className="!rounded-none border-none h-full" />
      </div>
      <div className="absolute bottom-0 inset-x-0 bg-[#0D0E10]/95 backdrop-blur border-t border-[#2E2F33] p-3">
        <p className="text-[9px] uppercase tracking-brand text-[#A0A0A5]">Punto seleccionado</p>
        <p className="text-[11px] font-body text-[#F5F5F5] mt-1">Av. Larco 1250, Miraflores</p>
        <button className="w-full mt-3 py-2.5 bg-[#C9A227] text-[#0D0E10] font-display font-bold uppercase tracking-wider text-[10px] rounded-lg">
          Confirmar destino
        </button>
      </div>
    </div>
  </PhoneMockup>
);

const MobileMockScreen4 = () => (
  <PhoneMockup label="Ruta optimizada · Conductor">
    <div className="h-full bg-[#0D0E10] flex flex-col relative">
      <div className="px-3 py-2 flex items-center justify-between">
        <p className="text-[9px] uppercase tracking-brand text-[#A0A0A5]">Ruta optimizada</p>
        <p className="text-[9px] font-display font-bold text-[#C9A227]">1h 24min · 38km</p>
      </div>
      <div className="flex-1 relative">
        <DarkMapMockup
          variant="route"
          path={optimizedRoute.path}
          pins={optimizedRoute.pins}
          className="!rounded-none border-none h-full"
        />
      </div>
      <div className="border-t border-[#2E2F33] bg-[#0D0E10] px-3 py-3 max-h-40 overflow-hidden">
        <div className="w-8 h-1 rounded-full bg-[#2E2F33] mx-auto mb-2" />
        {optimizedRoute.stops.slice(0, 2).map((s) => (
          <div key={s.n} className="flex items-center gap-2 py-1.5 border-b border-[#1C1D20]">
            <div className="w-5 h-5 rounded-full bg-[#C9A227] flex items-center justify-center text-[9px] font-display font-bold text-[#0D0E10]">{s.n}</div>
            <div className="min-w-0 flex-1">
              <p className="text-[10px] text-[#F5F5F5] truncate font-body">{s.client}</p>
              <p className="text-[9px] text-[#A0A0A5] truncate">{s.address}</p>
            </div>
            <p className="text-[9px] text-[#C9A227] font-display font-bold">{s.eta}</p>
          </div>
        ))}
      </div>
    </div>
  </PhoneMockup>
);

export default Landing;
